import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, GitBranch, ShoppingBag, Tags, Megaphone, Wallet, Settings, LogOut, Zap, BarChart3, Activity, Sun, Moon, MessagesSquare, ClipboardList, Menu,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/weekly-tasks", label: "Weekly Tasks", icon: ClipboardList },
  { to: "/affiliates", label: "Affiliates", icon: Users },
  { to: "/onboarding", label: "Status", icon: GitBranch },
  { to: "/orders", label: "Orders", icon: ShoppingBag },
  { to: "/codes", label: "Discount Codes", icon: Tags },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/payouts", label: "Payouts", icon: Wallet },
  { to: "/communications", label: "Communications", icon: MessagesSquare },
  { to: "/analytics", label: "Analytics", icon: Activity },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: Settings },
];

function ThemeToggle() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const stored = localStorage.getItem("theme");
    const initial = stored ? stored === "dark" : window.matchMedia("(prefers-color-scheme: dark)").matches;
    document.documentElement.classList.toggle("dark", initial);
    setDark(initial);
  }, []);
  const toggle = () => {
    const next = !dark;
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
    setDark(next);
  };
  return (
    <button
      data-testid="theme-toggle"
      onClick={toggle}
      className="flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium bg-secondary hover:bg-secondary/80 text-secondary-foreground transition-colors"
    >
      {dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
      {dark ? "Light" : "Dark"}
    </button>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <>
      <div className="px-6 py-6">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground">
            <Zap className="h-5 w-5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[15px] font-bold tracking-tight leading-none">Fuelled</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Affiliate CRM</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-border/60 space-y-3">
        <ThemeToggle />
        {user && (
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-orange-400 flex items-center justify-center text-primary-foreground text-xs font-bold">
              {user.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{user.name}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{user.role.replace("_", " ")}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { onNavigate?.(); await logout(); navigate("/login"); }}
              className="rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export function Sidebar() {
  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-border/60 bg-background/60 backdrop-blur-xl h-screen sticky top-0">
      <SidebarContent />
    </aside>
  );
}

export function MobileNav() {
  const [open, setOpen] = useState(false);
  return (
    <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 border-b border-border/60 bg-background/85 backdrop-blur-xl">
      <div className="flex items-center gap-2">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-nav-trigger"
              aria-label="Open navigation"
              className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            data-testid="mobile-nav-drawer"
            className="w-72 p-0 flex flex-col bg-background/95 backdrop-blur-xl border-r border-border/60"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarContent onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-xl bg-primary flex items-center justify-center text-primary-foreground">
            <Zap className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="text-sm font-bold tracking-tight">Fuelled</div>
        </div>
      </div>
    </div>
  );
}
